/**
 * CRMGuideModal Component
 * Modal com guia completo do CRM para iniciantes
 */

import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import {
  IconBook2,
  IconChevronDown,
  IconChevronRight,
  IconBulb,
  IconAlertTriangle,
  IconCircleCheck,
  IconArrowRight,
} from '@tabler/icons-react';
import './CRMGuideModal.css';

// ===============================================
// SECTION DATA
// ===============================================

const SECTIONS = [
  {
    id: 'what-is-crm',
    title: '1. O que é o CRM',
    content: (
      <>
        <p>
          CRM significa <strong>Customer Relationship Management</strong> (Gestão de Relacionamento com o Cliente).
          É uma ferramenta que ajuda você a <strong>organizar, acompanhar e gerenciar</strong> todos os seus contatos,
          negócios e vendas em um só lugar.
        </p>
        <p>
          Pense no CRM como um <strong>quadro organizado</strong> onde cada cartão representa um cliente ou oportunidade de negócio,
          e as colunas representam as etapas do seu processo de venda.
        </p>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Exemplo prático:</strong> Imagine que você vende imóveis. Um cliente entra em contato interessado.
            Você cria um cartão para ele no CRM, e conforme avança na negociação, vai movendo o cartão pelas colunas:
            "Novo Lead" → "Em Negociação" → "Proposta Enviada" → "Fechado".
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'pipelines',
    title: '2. Pipelines (Funis de Venda)',
    content: (
      <>
        <p>
          Um <strong>Pipeline</strong> é um funil de vendas completo. Ele representa um <strong>processo</strong> do início ao fim.
          Você pode ter vários pipelines para diferentes tipos de negócios.
        </p>
        <table className="guide-table">
          <thead>
            <tr><th>Exemplo de Pipeline</th><th>Para que serve</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Vendas de Produtos</strong></td><td>Acompanhar vendas de produtos físicos</td></tr>
            <tr><td><strong>Vendas de Serviços</strong></td><td>Acompanhar contratação de serviços</td></tr>
            <tr><td><strong>Pós-Venda</strong></td><td>Acompanhar suporte e fidelização</td></tr>
          </tbody>
        </table>
        <h4>Como trocar de Pipeline</h4>
        <p>
          No topo do CRM, há um <strong>seletor de pipeline</strong>. Clique nele para alternar entre seus funis.
          Cada pipeline tem suas próprias colunas e contatos.
        </p>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Dica:</strong> Se você tem apenas um tipo de negócio, um único pipeline é suficiente.
            Crie pipelines adicionais somente quando os processos forem realmente diferentes.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'columns',
    title: '3. Colunas (Etapas)',
    content: (
      <>
        <p>
          As <strong>colunas</strong> representam as etapas do seu processo de vendas. Cada coluna é uma fase pela qual o contato passa.
        </p>
        <h4>Tipos de Coluna</h4>
        <table className="guide-table">
          <thead>
            <tr><th>Tipo</th><th>Significado</th><th>Exemplo</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><span className="guide-tag guide-tag--blue">Aberto</span></td>
              <td>Negociação em andamento</td>
              <td>Novo Lead, Em Negociação, Proposta Enviada</td>
            </tr>
            <tr>
              <td><span className="guide-tag guide-tag--green">Ganho</span></td>
              <td>Venda concluída com sucesso</td>
              <td>Fechado/Ganho</td>
            </tr>
            <tr>
              <td><span className="guide-tag guide-tag--red">Perdido</span></td>
              <td>Negócio que não avançou</td>
              <td>Perdido, Desistiu</td>
            </tr>
          </tbody>
        </table>
        <h4>Dias Máximos (Estagnação)</h4>
        <p>
          Ao criar uma coluna, você pode definir um <strong>número máximo de dias</strong> que um contato pode ficar nela.
          Se ultrapassar, o contato é considerado <strong>estagnado</strong> — indicando que precisa de atenção.
        </p>
        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Atenção:</strong> Quando um contato é movido para uma coluna do tipo "Ganho", a data de ganho é registrada
            automaticamente. O mesmo vale para "Perdido". Isso alimenta os indicadores do Dashboard.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'views',
    title: '4. Modos de Visualização',
    content: (
      <>
        <p>O CRM oferece <strong>3 modos</strong> para visualizar seus dados. Use o que for mais confortável para cada situação:</p>
        <table className="guide-table">
          <thead>
            <tr><th>Modo</th><th>O que é</th><th>Melhor para</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Board (Kanban)</strong></td>
              <td>Quadro com colunas e cartões que você arrasta entre etapas</td>
              <td>Visão geral do funil, mover contatos entre etapas</td>
            </tr>
            <tr>
              <td><strong>Lista</strong></td>
              <td>Tabela com todos os contatos em linhas</td>
              <td>Ver muitos contatos de uma vez, ordenar, buscar rapidamente</td>
            </tr>
            <tr>
              <td><strong>Dashboard</strong></td>
              <td>Painel de indicadores e gráficos</td>
              <td>Analisar desempenho, ver métricas de vendas</td>
            </tr>
          </tbody>
        </table>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Board:</strong> Arraste os cartões de uma coluna para outra para mover o contato de etapa.
            Role para baixo dentro de uma coluna para carregar mais contatos (scroll infinito).
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'contacts',
    title: '5. Contatos (Cards)',
    content: (
      <>
        <p>
          Cada <strong>contato</strong> é um cartão no board (ou uma linha na lista). Ele representa uma pessoa ou empresa
          com quem você está negociando.
        </p>
        <h4>Informações do Contato</h4>
        <table className="guide-table">
          <thead>
            <tr><th>Campo</th><th>O que é</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Nome</strong></td><td>Nome do contato ou empresa</td></tr>
            <tr><td><strong>Telefone / Email</strong></td><td>Dados para contato</td></tr>
            <tr><td><strong>Empresa</strong></td><td>Empresa onde o contato trabalha</td></tr>
            <tr><td><strong>Valor do Negócio</strong></td><td>Quanto vale esta oportunidade em R$</td></tr>
            <tr><td><strong>Prioridade</strong></td><td>Alta, Média ou Baixa — indica urgência</td></tr>
            <tr><td><strong>Temperatura</strong></td><td>Quente, Morno ou Frio — indica nível de interesse</td></tr>
            <tr><td><strong>Responsável</strong></td><td>Membro da equipe que cuida deste contato</td></tr>
            <tr><td><strong>Tags</strong></td><td>Etiquetas para categorizar (ex: "VIP", "Indicação")</td></tr>
          </tbody>
        </table>
        <h4>Drawer (Painel Lateral)</h4>
        <p>
          Ao clicar em um cartão, abre um <strong>painel lateral</strong> com todos os detalhes do contato,
          onde você pode editar informações, ver o histórico de atividades e adicionar notas.
        </p>
      </>
    ),
  },
  {
    id: 'filters',
    title: '6. Filtros e Busca',
    content: (
      <>
        <p>
          Os filtros permitem <strong>reduzir a visualização</strong> para mostrar apenas os contatos que atendem a critérios específicos,
          tanto no Board (Kanban) quanto na Lista.
        </p>
        <h4>Filtros Disponíveis</h4>
        <table className="guide-table">
          <thead>
            <tr><th>Filtro</th><th>O que faz</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Responsável</strong></td><td>Mostra apenas contatos de um membro específico da equipe</td></tr>
            <tr><td><strong>Prioridade</strong></td><td>Filtra por Alta, Média ou Baixa</td></tr>
            <tr><td><strong>Temperatura</strong></td><td>Filtra por Quente, Morno ou Frio</td></tr>
            <tr><td><strong>Valor do Negócio</strong></td><td>Filtra por faixa de valor (mínimo e máximo)</td></tr>
            <tr><td><strong>Apenas Estagnados</strong></td><td>Mostra somente contatos que ultrapassaram o tempo máximo na coluna</td></tr>
            <tr><td><strong>Ordenar por</strong></td><td>Altera a ordem dos contatos (por valor, data, nome, prioridade)</td></tr>
          </tbody>
        </table>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Como usar:</strong> Clique no ícone de filtro no cabeçalho do CRM. Configure os filtros desejados
            e clique em "Aplicar". Para voltar a ver tudo, clique em "Limpar".
          </div>
        </div>
        <h4>Busca Rápida</h4>
        <p>
          Use a <strong>barra de busca</strong> no topo para encontrar contatos por nome, telefone, email ou empresa.
          A busca filtra os cartões no board em tempo real.
        </p>
      </>
    ),
  },
  {
    id: 'saved-views',
    title: '7. Views Salvas',
    content: (
      <>
        <p>
          As <strong>Views Salvas</strong> permitem guardar uma combinação de filtros com um nome, para reaplicá-los rapidamente
          sem precisar configurar cada filtro novamente.
        </p>
        <h4>Como usar</h4>
        <div className="guide-flow-steps">
          <div className="guide-flow-step">Configure os filtros</div>
          <IconArrowRight size={14} className="guide-flow-arrow" />
          <div className="guide-flow-step">Clique em "Salvar View"</div>
          <IconArrowRight size={14} className="guide-flow-arrow" />
          <div className="guide-flow-step">Dê um nome</div>
          <IconArrowRight size={14} className="guide-flow-arrow" />
          <div className="guide-flow-step">Pronto! Clique na aba para reaplicar</div>
        </div>
        <div className="guide-callout guide-callout--success">
          <IconCircleCheck size={16} />
          <div>
            <strong>Exemplo:</strong> Salve uma view "Meus deals quentes" com filtro Responsável = Você + Temperatura = Quente.
            Sempre que precisar, basta clicar na aba para ver exatamente esses contatos.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'dashboard',
    title: '8. Dashboard (Indicadores)',
    content: (
      <>
        <p>
          O Dashboard mostra <strong>indicadores de desempenho</strong> (KPIs) do seu pipeline em um período selecionado (padrão: últimos 30 dias).
        </p>
        <h4>KPIs do Dashboard</h4>
        <table className="guide-table">
          <thead>
            <tr><th>Indicador</th><th>O que significa</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>Deals Abertos</strong></td><td>Quantidade de negócios em andamento (colunas do tipo "Aberto")</td></tr>
            <tr><td><strong>Valor Total</strong></td><td>Soma do valor de todos os deals abertos</td></tr>
            <tr><td><strong>Valor Ponderado</strong></td><td>Valor total ajustado pela probabilidade de fechamento de cada etapa</td></tr>
            <tr><td><strong>Ganhos</strong></td><td>Quantidade de negócios fechados com sucesso no período</td></tr>
            <tr><td><strong>Taxa de Conversão</strong></td><td>Percentual de deals ganhos em relação ao total de deals fechados (ganhos + perdidos)</td></tr>
            <tr><td><strong>Ticket Médio</strong></td><td>Valor médio dos negócios ganhos</td></tr>
            <tr><td><strong>Novos Deals</strong></td><td>Quantidade de contatos criados no período</td></tr>
            <tr><td><strong>Ciclo Médio</strong></td><td>Tempo médio entre a criação do contato e o fechamento (em dias)</td></tr>
          </tbody>
        </table>
        <h4>Funil de Conversão</h4>
        <p>
          O gráfico de funil mostra quantos contatos estão em cada etapa do pipeline e o valor total em cada uma.
          Isso ajuda a identificar <strong>gargalos</strong> — etapas onde os contatos ficam parados.
        </p>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Dica:</strong> Se muitos contatos ficam parados em "Proposta Enviada" mas poucos avançam para "Fechado",
            talvez você precise melhorar suas propostas ou fazer follow-up mais frequente.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'activities',
    title: '9. Atividades e Notas',
    content: (
      <>
        <p>
          As <strong>atividades</strong> são tarefas que você agenda para um contato: ligações, reuniões, follow-ups, emails.
          Elas ajudam a manter o <strong>acompanhamento organizado</strong>.
        </p>
        <h4>Status de Atividade</h4>
        <table className="guide-table">
          <thead>
            <tr><th>Status</th><th>Significado</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><span className="guide-tag guide-tag--amber">Pendente</span></td>
              <td>Atividade agendada, ainda não realizada</td>
            </tr>
            <tr>
              <td><span className="guide-tag guide-tag--red">Atrasada</span></td>
              <td>A data passou e a atividade não foi concluída</td>
            </tr>
            <tr>
              <td><span className="guide-tag guide-tag--green">Concluída</span></td>
              <td>Atividade já realizada</td>
            </tr>
          </tbody>
        </table>
        <h4>Notas</h4>
        <p>
          Use <strong>notas</strong> para registrar informações importantes sobre conversas, preferências do cliente
          ou qualquer observação relevante. As notas ficam no histórico do contato.
        </p>
        <div className="guide-callout guide-callout--success">
          <IconCircleCheck size={16} />
          <div>
            <strong>Boa prática:</strong> Após cada interação com o cliente (ligação, reunião, WhatsApp), registre uma nota
            resumindo o que foi conversado. Isso ajuda toda a equipe a saber o histórico sem precisar perguntar.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'bulk-actions',
    title: '10. Ações em Massa',
    content: (
      <>
        <p>
          Você pode selecionar <strong>múltiplos contatos</strong> e executar ações neles de uma vez, economizando tempo.
        </p>
        <h4>Como selecionar</h4>
        <p>
          No modo <strong>Board</strong>, clique no checkbox de cada cartão que deseja selecionar.
          No modo <strong>Lista</strong>, clique nos checkboxes na tabela. Uma barra de ações aparecerá no topo.
        </p>
        <h4>Ações disponíveis</h4>
        <ul>
          <li><strong>Mover para coluna</strong> — Move todos os selecionados para uma etapa específica</li>
          <li><strong>Alterar responsável</strong> — Atribui um membro da equipe aos contatos</li>
          <li><strong>Excluir</strong> — Remove os contatos selecionados</li>
        </ul>
        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Cuidado:</strong> A exclusão em massa é irreversível. Confirme sempre antes de excluir.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'automations',
    title: '11. Automações de Follow-up',
    content: (
      <>
        <p>
          As <strong>Automações</strong> executam ações automaticamente quando algum evento acontece no CRM:
          contato entra em uma coluna, recebe uma tag, é criado, etc. Servem para escalar o atendimento sem
          deixar nenhum lead esfriar.
        </p>
        <div className="guide-callout guide-callout--info">
          <IconBulb size={16} />
          <div>
            <strong>Como acessar:</strong> No topo do CRM, clique no ícone de raio
            (⚡ <em>Automações</em>) ao lado dos botões Kanban / Lista / Dashboard.
            Lá você vê, ativa/desativa, edita e cria novas automações do pipeline selecionado.
          </div>
        </div>

        <h4>Estrutura: Gatilho + Ação</h4>
        <p>
          Toda automação tem duas partes obrigatórias:
        </p>
        <ul>
          <li><strong>Gatilho (quando disparar):</strong> o evento que aciona a automação.</li>
          <li><strong>Ação (o que fazer):</strong> o que o sistema executa quando o gatilho acontece.</li>
        </ul>

        <h4>Gatilhos disponíveis</h4>
        <table className="guide-table">
          <thead>
            <tr><th>Gatilho</th><th>Quando dispara</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Contato entrou no estágio</strong></td>
              <td>Quando um contato é movido para a coluna escolhida. Permite criar fluxos como "ao entrar em Quente, envie boas-vindas".</td>
            </tr>
            <tr>
              <td><strong>Contato criado</strong></td>
              <td>Quando qualquer contato novo é criado no pipeline. Útil para sequência de boas-vindas.</td>
            </tr>
            <tr>
              <td><strong>Tag adicionada / removida</strong></td>
              <td>Quando uma tag específica é colocada (ou retirada) do contato. Você escolhe quais tags disparam — não é qualquer tag.</td>
            </tr>
            <tr>
              <td><strong>Deal estagnado</strong></td>
              <td>Quando o contato fica parado em uma coluna por X tempo (minutos, horas ou dias). Útil para reengajar leads frios.</td>
            </tr>
          </tbody>
        </table>

        <h4>Ações disponíveis</h4>
        <table className="guide-table">
          <thead>
            <tr><th>Ação</th><th>O que faz</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Enviar mensagem WhatsApp</strong></td>
              <td>Envia uma mensagem pelo WhatsApp conectado da sua conta (Não oficial ou API oficial Meta).</td>
            </tr>
            <tr>
              <td><strong>Enviar e-mail</strong></td>
              <td>Envia um e-mail usando o SMTP configurado da sua organização. Aceita Assunto e Corpo.</td>
            </tr>
            <tr>
              <td><strong>Mover para estágio</strong></td>
              <td>Move o contato automaticamente para outra coluna do pipeline.</td>
            </tr>
            <tr>
              <td><strong>Atribuir responsável</strong></td>
              <td>Define um membro da equipe como responsável pelo contato.</td>
            </tr>
            <tr>
              <td><strong>Adicionar / Remover tag</strong></td>
              <td>Coloca ou retira uma tag específica do contato.</td>
            </tr>
            <tr>
              <td><strong>Criar atividade</strong></td>
              <td>Agenda uma atividade futura (ligação, tarefa, reunião, e-mail, nota) para o responsável.</td>
            </tr>
            <tr>
              <td><strong>Ativar / Desativar bot</strong></td>
              <td>Liga ou desliga o atendimento automático do agente de IA para esse contato.</td>
            </tr>
          </tbody>
        </table>

        <h4>Atraso (delay)</h4>
        <p>
          Mensagens (WhatsApp/e-mail) e atividades podem ter um atraso configurável: <strong>minutos, horas ou dias</strong>.
          Use 0 para envio imediato. Exemplo: "ao entrar em Quente, enviar WhatsApp depois de 5 minutos".
          Sequências de mensagens espaçadas (drip) podem ser feitas com várias automações no mesmo gatilho usando atrasos crescentes.
        </p>

        <h4>Variáveis na mensagem</h4>
        <p>
          As mensagens (WhatsApp/e-mail) e o título de atividades suportam variáveis substituídas no envio:
        </p>
        <table className="guide-table">
          <thead>
            <tr><th>Variável</th><th>Substituída por</th></tr>
          </thead>
          <tbody>
            <tr><td><code>{"{nome}"}</code></td><td>Nome completo do contato</td></tr>
            <tr><td><code>{"{primeiro_nome}"}</code></td><td>Primeira palavra do nome</td></tr>
            <tr><td><code>{"{email}"}</code></td><td>E-mail do contato</td></tr>
            <tr><td><code>{"{telefone}"}</code></td><td>Telefone do contato</td></tr>
          </tbody>
        </table>
        <p>
          Exemplo: <code>Olá &#123;primeiro_nome&#125;, vi que você se interessou!</code>
          é enviada como <code>Olá João, vi que você se interessou!</code>
        </p>

        <h4>Casos de uso comuns</h4>
        <ul>
          <li><strong>Boas-vindas:</strong> gatilho <em>Contato criado</em> → ação <em>Enviar WhatsApp</em> (atraso 0)</li>
          <li><strong>Drip (sequência):</strong> mesmo gatilho com 3 automações de WhatsApp em atrasos 0, 1 dia e 3 dias</li>
          <li><strong>Reengajamento:</strong> <em>Deal estagnado</em> 7 dias → <em>Enviar WhatsApp</em></li>
          <li><strong>Roteamento automático:</strong> <em>Tag adicionada</em> ("VIP") → <em>Atribuir responsável</em></li>
          <li><strong>Avanço automático:</strong> <em>Contato entrou em Quente</em> → <em>Criar atividade</em> "Ligar para o cliente" em 24h</li>
        </ul>

        <div className="guide-callout guide-callout--warning">
          <IconAlertTriangle size={16} />
          <div>
            <strong>Atenção:</strong> automações só disparam quando estão <strong>ativas</strong> (o switch azul ligado).
            Ao criar uma automação você pode deixá-la inativa para testar antes. Cada automação mostra o número de
            execuções acumuladas — útil para verificar se está rodando como esperado.
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'best-practices',
    title: '12. Boas Práticas',
    content: (
      <>
        <h4>Organização do Pipeline</h4>
        <ul>
          <li><strong>Mantenha poucas etapas</strong> — 4 a 7 colunas são o ideal. Muitas etapas confundem</li>
          <li><strong>Use nomes claros</strong> — "Proposta Enviada" é melhor que "Etapa 3"</li>
          <li><strong>Defina dias máximos</strong> — Configure estagnação para não esquecer de contatos parados</li>
          <li><strong>Tenha colunas de Ganho e Perda</strong> — Mova contatos finalizados para lá, mantendo o funil limpo</li>
        </ul>

        <h4>Gestão dos Contatos</h4>
        <ul>
          <li><strong>Preencha o valor do negócio</strong> — Isso alimenta os indicadores financeiros do Dashboard</li>
          <li><strong>Defina prioridade e temperatura</strong> — Facilita filtrar e priorizar seu tempo</li>
          <li><strong>Atribua um responsável</strong> — Cada contato deve ter um dono claro na equipe</li>
          <li><strong>Registre atividades</strong> — Agende follow-ups para não deixar nenhum contato esfriar</li>
        </ul>

        <h4>Acompanhamento</h4>
        <ul>
          <li><strong>Verifique estagnados diariamente</strong> — Use o filtro "Apenas estagnados" para ver quem precisa de atenção</li>
          <li><strong>Revise o Dashboard semanalmente</strong> — Acompanhe taxa de conversão e ticket médio</li>
          <li><strong>Salve views para rotinas</strong> — Crie views como "Meus pendentes" ou "Deals quentes" para acesso rápido</li>
        </ul>

        <div className="guide-callout guide-callout--success">
          <IconCircleCheck size={16} />
          <div>
            <strong>Resumo:</strong> O CRM funciona melhor quando é usado <strong>todos os dias</strong>.
            Atualize os contatos conforme as negociações avançam, registre atividades e mova os cartões entre as colunas.
            Quanto mais você usar, melhores serão os indicadores e mais controle você terá sobre suas vendas.
          </div>
        </div>
      </>
    ),
  },
];

// ===============================================
// ACCORDION SECTION
// ===============================================

const GuideSection = ({ section, isOpen, onToggle }) => {
  return (
    <div className={`guide-section ${isOpen ? 'guide-section--open' : ''}`}>
      <button className="guide-section__header" onClick={onToggle}>
        <span className="guide-section__title">{section.title}</span>
        {isOpen ? <IconChevronDown size={18} /> : <IconChevronRight size={18} />}
      </button>
      {isOpen && (
        <div className="guide-section__content">
          {section.content}
        </div>
      )}
    </div>
  );
};

// ===============================================
// MAIN MODAL
// ===============================================

const CRMGuideModal = ({ show, onHide }) => {
  const [openSections, setOpenSections] = useState(['what-is-crm']);

  const toggleSection = (id) => {
    setOpenSections((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const expandAll = () => setOpenSections(SECTIONS.map((s) => s.id));
  const collapseAll = () => setOpenSections([]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      className="user-guide-modal"
      scrollable
    >
      <Modal.Header closeButton className="user-guide-modal__header">
        <Modal.Title className="user-guide-modal__title">
          <div className="user-guide-modal__title-icon">
            <IconBook2 size={22} />
          </div>
          Guia Prático do CRM
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="user-guide-modal__body">
        <p className="user-guide-modal__intro">
          Este guia explica todos os recursos do CRM de forma simples e prática.
          Se é sua primeira vez, comece pela seção 1. Clique em cada seção para expandir.
        </p>

        <div className="user-guide-modal__controls">
          <button className="user-guide-modal__control-btn" onClick={expandAll}>
            Expandir tudo
          </button>
          <button className="user-guide-modal__control-btn" onClick={collapseAll}>
            Recolher tudo
          </button>
        </div>

        <div className="guide-sections">
          {SECTIONS.map((section) => (
            <GuideSection
              key={section.id}
              section={section}
              isOpen={openSections.includes(section.id)}
              onToggle={() => toggleSection(section.id)}
            />
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer className="user-guide-modal__footer">
        <Button variant="primary" size="sm" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CRMGuideModal;
